// src/App.js
// import React, { useEffect, useState } from "react";
import React, { useEffect } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
// import bgImageLight from "./supermarket-bg.jpeg";
// import bgImageDark from "./supermarket-bg.jpeg";
// import AuthButtons from "./components/AuthButtons";
import { modules, moduleBySlug } from "./config/modules";
import ModuleDashboard from "./pages/ModuleDashboard";
import LogoutScreen from "./components/LogoutScreen";
import HashRedirect from "./components/HashRedirect";
import SdkDashboardTest from "./pages/SdkDashboardTest";

function hexToRGBA(hex, alpha = 0.15) {
  try {
    let h = hex?.toString().trim();
    if (!h) return `rgba(0,0,0,${alpha})`;
    if (h[0] === "#") h = h.slice(1);
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) throw new Error();
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return `rgba(2, 6, 23, ${alpha})`; // slate-950 @ ~15%
  }
}

const ButtonCard = ({ title, color = "#2563eb", icon, description, to, href, newTab }) => {
  const iconBg = hexToRGBA(color, 0.16);

  const content = (
    <div className="bg-white p-6 rounded-xl shadow-lg card-hover flex items-center gap-4">
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: iconBg, color }}
      >
        <span className="text-2xl leading-none">{icon}</span>
      </div>
      <div className="text-base font-semibold text-gray-900">{title}</div>
    </div>
  );

  // Wrapper conserva tu tooltip al hover
  const Wrapper = ({ children }) => (
    <div className="group relative z-0 hover:z-30 focus-within:z-30">
      {children}
      <div className="absolute top-full left-0 right-0 mt-2 z-50 pointer-events-none">
        <div
          className="mx-auto w-[calc(100%-0.75rem)] max-w-[28rem] px-3 py-2 text-xs leading-snug text-white
                     bg-black/80 rounded-lg shadow-lg backdrop-blur opacity-0
                     group-hover:opacity-100 group-focus-within:opacity-100 transition
                     whitespace-normal break-words text-center"
        >
          {description}
        </div>
      </div>
    </div>
  );

  if (to) {
    return (
      <Wrapper>
        <Link to={to} className="block">{content}</Link>
      </Wrapper>
    );
  }
  if (href) {
    return (
      <Wrapper>
        <a
          href={href}
          target={newTab ? "_blank" : undefined}
          rel={newTab ? "noopener noreferrer" : undefined}
          className="block"
          aria-label={`${title}${newTab ? " (se abre en una nueva pestaña)" : ""}`}
        >
          {content}
        </a>
      </Wrapper>
    );
  }
  return <Wrapper>{content}</Wrapper>;
};


function Home() {
  const { isLoading, isAuthenticated, loginWithRedirect, logout, user } = useAuth0();

  // sombreado del navbar al hacer scroll
  useEffect(() => {
    const onScroll = () => {
      const nav = document.getElementById("top-nav");
      if (!nav) return;
      if (window.scrollY > 8) nav.classList.add("shadow-lg");
      else nav.classList.remove("shadow-lg");
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const heroBg =
    "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=2074&q=80";

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {/* NAV */}
      <nav id="top-nav" className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <svg className="h-8 w-8 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 3v18h18V3H3zm16 16H5V5h14v14zM7 7h2v8H7V7zm4 0h2v8h-2V7zm4 2h2v6h-2V9z" />
                </svg>
                <span className="text-xl font-bold text-gray-900">UniBi Proveedores</span>
              </div>
            </div>

            {/* Links + botón de sesión con buen contraste */}
            <div className="hidden md:flex items-center gap-4">
              {/* <a href="/guia" className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium transition-colors">
                Guía de Usuario
              </a>
              <a href="/glosario" className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium transition-colors">
                Glosario
              </a> */}

               {!isLoading && isAuthenticated && (
                <span className="text-gray-600 text-sm hidden sm:inline">
                  Hola, <strong>{user?.name || user?.email}</strong>
                </span>
              )}

              <button
                onClick={() =>
                  isAuthenticated
                    ? logout({ returnTo: window.location.origin })
                    : loginWithRedirect()
                }
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                {isAuthenticated ? "Cerrar Sesión" : "Iniciar Sesión"}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO — paddings reducidos */}
      <section
        className="pt-16 pb-8 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${heroBg}')` }}
      >
        <div className="absolute inset-0 bg-white/80"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              Bienvenido a <span className="text-green-600">UniBi Proveedores</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-4 max-w-3xl mx-auto">
              Desbloquea el poder de tus datos con nuestra plataforma de análisis avanzada.
              Toma decisiones basadas en datos reales y reportes automatizados.
            </p>
          </div>
        </div>
      </section>

      {/* STATS — compacto */}
      {/* <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
            <div className="pulse-animation">
              <div className="text-3xl md:text-4xl font-bold text-indigo-600 mb-1">24/7</div>
              <div className="text-gray-600">Soporte</div>
            </div>
            <div className="pulse-animation">
              <div className="text-3xl md:text-4xl font-bold text-indigo-600 mb-1">400+</div>
              <div className="text-gray-600">Proveedores con Acceso</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* GRID DE CARDS — más cerca del pliegue */}
      <section className="bg-gray-50 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {modules.map((m) => (
              <ButtonCard
                key={m.slug}
                title={m.title}
                color={m.color}
                icon={m.icon}
                description={m.description} // mantiene tu tooltip al hover
                to={!m.external ? `/m/${m.slug}` : undefined}
                href={m.external ? m.externalUrl : undefined}
                newTab={m.external}
              />
            ))}
          </div>

          {isLoading && (
            <div className="text-center text-gray-600 mt-8">Cargando sesión...</div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base md:text-lg text-gray-300 leading-relaxed">
            Si deseas obtener más información sobre las licencias disponibles en UniBi puedes
            contactar a Andrea Escobar al correo{" "}
            <a
              href="mailto:aescobar@unisupergt.com"
              className="text-indigo-400 hover:text-indigo-300 transition-colors underline"
            >
              aescobar@unisupergt.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}


// Redirect /m/:moduleSlug → its default (or first) dashboard
function ModuleRedirect({ moduleSlug }) {
  const mod = moduleBySlug(moduleSlug);
  if (!mod || !mod.dashboards?.length) return <Navigate to="/" replace />;
  const target = mod.defaultDashboard || mod.dashboards[0].slug;
  return <Navigate to={`/m/${mod.slug}/${target}`} replace />;
}

function AppRoutes() {
  return (
    <>
      <HashRedirect />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sdk-test" element={<SdkDashboardTest />} />
        <Route path="/logout" element={<LogoutScreen />} />
        <Route
          path="/m/:moduleSlug"
          element={
            <RouteWrapper
              render={({ moduleSlug }) => <ModuleRedirect moduleSlug={moduleSlug} />}
            />
          }
        />
        <Route path="/m/:moduleSlug/:dashboardSlug" element={<ModuleDashboard />} />
        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// tiny helper to read params in inline elements
function RouteWrapper({ render }) {
  return (
    <ParamsBridge render={render} />
  );
}
function ParamsBridge({ render }) {
  const params = require("react-router-dom").useParams();
  return render(params);
}

export default withAuthenticationRequired(AppRoutes, {
  onRedirecting: () => (
    <div className="min-h-screen flex items-center justify-center text-white">
      Redirigiendo al login…
    </div>
  ),
});
